import { useTranslation } from 'react-i18next';
import snakeCase from 'lodash/snakeCase';
import { DateTime } from 'luxon';

import {
  type DateTimeField,
  type DateTimeFieldFormat,
  type EquationField
} from '@/types/schema/fields';
import { type KnackCriteria, type KnackCriteriaDateTimeValue } from '@/types/schema/KnackCriteria';
import { getLuxonDateFormatFromFieldFormat } from '@/components/knack-date-picker/transformers/DateTimeFormatToLuxonFormat';
import {
  sanitizeTimeString,
  sanitizeTimeStringFromHoursMinutes
} from '@/components/knack-date-picker/utils';

export function useCriteriaDateTimeHelpers() {
  const [t] = useTranslation();

  const getDefaultCriteriaTimeValues = (dateTimeField: DateTimeField | EquationField) => {
    const dateTimeFieldFormat: DateTimeFieldFormat = dateTimeField.format;

    if (
      dateTimeFieldFormat.time_format === 'Ignore Time' ||
      dateTimeFieldFormat.time_type === 'none'
    ) {
      const criteriaTimeValues: KnackCriteriaDateTimeValue = {
        time: '',
        hours: '',
        minutes: '',
        am_pm: ''
      };

      return criteriaTimeValues;
    }

    const defaultFormat =
      dateTimeFieldFormat.time_format === 'HH MM (military)' ? 'HH:mm' : 'h:mma';

    // If the field is an equation field that can store times, return the current time since there can't be a default time
    if (dateTimeField.type === 'equation' || dateTimeFieldFormat.time_type === 'current') {
      const now = DateTime.now();

      const criteriaTimeValues: KnackCriteriaDateTimeValue = {
        time: now.toFormat(defaultFormat).toLowerCase(),
        hours: now.toFormat('H'),
        minutes: now.toFormat('m'),
        am_pm: now.toFormat('a') as 'AM' | 'PM'
      };

      return criteriaTimeValues;
    }

    if (dateTimeFieldFormat.time_type === 'time' && dateTimeFieldFormat.default_time) {
      const defaultTime =
        sanitizeTimeString(dateTimeFieldFormat.default_time, dateTimeFieldFormat) || '';

      const luxonDate = DateTime.fromFormat(defaultTime, defaultFormat);

      if (luxonDate.isValid) {
        const criteriaTimeValues: KnackCriteriaDateTimeValue = {
          time: luxonDate.toFormat(defaultFormat).toLowerCase(),
          hours: luxonDate.toFormat('H'),
          minutes: luxonDate.toFormat('m'),
          am_pm: luxonDate.toFormat('a') as 'AM' | 'PM'
        };

        return criteriaTimeValues;
      }
    }

    return '';
  };

  const getDefaultCriteriaDateValue = (dateTimeField: DateTimeField | EquationField) => {
    const dateTimeFieldFormat: DateTimeFieldFormat = dateTimeField.format;
    const normalizedDateFormat = getLuxonDateFormatFromFieldFormat(dateTimeFieldFormat.date_format);

    if (!normalizedDateFormat) {
      return '';
    }

    // If the field is an equation field that can store dates, return the current date since there can't be a default date
    if (dateTimeField.type === 'equation') {
      return DateTime.now().toFormat(normalizedDateFormat);
    }

    if (dateTimeFieldFormat.default_type === 'date' && dateTimeFieldFormat.default_date) {
      const formattedDate = DateTime.fromFormat(
        dateTimeFieldFormat.default_date,
        normalizedDateFormat
      ).toFormat(normalizedDateFormat);

      return formattedDate;
    }

    if (dateTimeFieldFormat.default_type === 'current') {
      return DateTime.now().toFormat(normalizedDateFormat);
    }

    return '';
  };

  const getDefaultCriteriaDateTimeValue = (dateTimeField: DateTimeField | EquationField) => {
    const dateTimeFieldFormat: DateTimeFieldFormat = dateTimeField.format;

    const defaultDate: KnackCriteriaDateTimeValue = {
      date: getDefaultCriteriaDateValue(dateTimeField),

      // Only include time properties if the time is not ignored
      ...(dateTimeFieldFormat.time_format !== 'Ignore Time' && {
        ...getDefaultCriteriaTimeValues(dateTimeField)
      })
    };
    return defaultDate;
  };

  const getCriteriaDateTimeDisplayString = (
    criteria: KnackCriteria,
    criteriaField: DateTimeField | EquationField
  ) => {
    const { operator, type, range, value } = criteria;

    if (
      operator === 'is during the previous' ||
      operator === 'is during the next' ||
      operator === 'is before the previous' ||
      operator === 'is after the next'
    ) {
      if (!type || !range) {
        return '-';
      }

      return t(`components.view_filters.date_time.number_${snakeCase(type)}`, {
        number: range
      });
    }

    if (operator === 'is during the current') {
      if (!type) {
        return '-';
      }

      return t(`components.view_filters.date_time.${type}`);
    }

    if (
      criteriaField.format.date_format === 'Ignore Date' &&
      criteriaField.format.time_format === 'Ignore Time'
    ) {
      return '-';
    }

    // If the criteria value is not an object or does not have a date property, return an empty string
    if (typeof value !== 'object' || !('date' in value)) {
      return '-';
    }

    if (criteriaField.format.date_format === 'Ignore Date') {
      return sanitizeTimeStringFromHoursMinutes(
        value.hours,
        value.minutes,
        value.am_pm,
        criteriaField.format
      );
    }

    if (criteriaField.format.time_format === 'Ignore Time') {
      return value.date || '-';
    }

    if (value.date === '' && value.time === '') {
      return t('components.view_filters.date_time.any_date_any_time');
    }

    const timeString = sanitizeTimeStringFromHoursMinutes(
      value.hours,
      value.minutes,
      value.am_pm,
      criteriaField.format
    );
    return `${value.date || ''} ${timeString}`.trim();
  };

  return {
    getDefaultCriteriaTimeValues,
    getDefaultCriteriaDateTimeValue,
    getDefaultCriteriaDateValue,
    getCriteriaDateTimeDisplayString
  };
}
